import { useMutation, useQuery } from "@tanstack/react-query";
import {
	disableTwoFactorAuth,
	generateQrCodeOTP,
	twoFactorRequestOTP,
	verifySpaceUsernameExists,
} from "../control/2factor.spaceApi";
import type {
	ISpace2FactorRequest,
	ISpaceGenerateQrCodeRequest,
	ISpaceOtpUserLookupRequest,
} from "../dtos/2factor.dto";

// ------------------------------- CONSTANTS -------------------------------
export const keys_2factor = {
	base: ["space"] as const,
	security: () => [...keys_2factor.base, "security"] as const,
	twoFactor: () => [...keys_2factor.security(), "2fa"] as const,
	qrCode: (payload: ISpaceGenerateQrCodeRequest) =>
		[
			...keys_2factor.twoFactor(),
			"qr-code",
			payload.nome_usuario.trim(),
			payload.id_cad_grupo_bloqueado?.trim() ?? "",
			payload.groupId?.trim() ?? "",
		] as const,
	requestOtp: () => [...keys_2factor.twoFactor(), "request-otp"] as const,
	generateQrCode: () => [...keys_2factor.twoFactor(), "generate-qr-code"] as const,
	disable: () => [...keys_2factor.twoFactor(), "disable"] as const,
	verifyUsername: () => [...keys_2factor.twoFactor(), "verify-username"] as const,
};

// --------------------------------- TYPES ---------------------------------
type ConsultaUsuarioSpace = ISpaceOtpUserLookupRequest & { readonly signal?: AbortSignal };

type TwoFactorRequestArgs = {
	payload: ISpace2FactorRequest;
	isSpaceUser?: boolean;
};

// -------------------------------- HELPERS --------------------------------
function normalizarPayloadQrCode(payload: ISpaceGenerateQrCodeRequest): ISpaceGenerateQrCodeRequest {
	return {
		nome_usuario: payload.nome_usuario.trim(),
		id_cad_grupo_bloqueado: payload.id_cad_grupo_bloqueado?.trim() ?? "",
		groupId: payload.groupId?.trim() ?? "",
	};
}

// ------------------------------------------------------------------- OTP
export function useTwoFactorRequestOTP() {
	return useMutation({
		mutationKey: keys_2factor.requestOtp(),
		mutationFn: ({ payload, isSpaceUser = false }: TwoFactorRequestArgs) =>
			twoFactorRequestOTP(payload, isSpaceUser),
		retry: false,
	});
}

export function useVerifySpaceUsernameExists(contexto: "sessao" | "login" = "sessao") {
	return useMutation({
		mutationKey: keys_2factor.verifyUsername(),
		mutationFn: ({ signal, ...payload }: ConsultaUsuarioSpace) =>
			verifySpaceUsernameExists(payload, { contexto, signal }),
		retry: false,
		meta: { silenciarErro: contexto === "login" },
	});
}

// ------------------------------------------------------------------- QR Code
export function useGenerateQrCodeOTP() {
	return useMutation({
		mutationKey: keys_2factor.generateQrCode(),
		mutationFn: (payload: ISpaceGenerateQrCodeRequest) => generateQrCodeOTP(payload),
		retry: false,
	});
}

export function useQrCodeOTP(payload: ISpaceGenerateQrCodeRequest, enabled = true) {
	const payloadNormalizado = normalizarPayloadQrCode(payload);
	const podeBuscar = payloadNormalizado.nome_usuario.length > 0;

	return useQuery({
		queryKey: keys_2factor.qrCode(payloadNormalizado),
		queryFn: () => generateQrCodeOTP(payloadNormalizado),
		enabled: enabled && podeBuscar,
		retry: 0,
	});
}

// ------------------------------------------------------------------- Desabilitar
export function useDisableTwoFactorAuth() {
	return useMutation({
		mutationKey: keys_2factor.disable(),
		mutationFn: (payload: { nome_usuario: string; groupId: string }) => disableTwoFactorAuth(payload),
		retry: false,
	});
}
